// Custom Flow Node for React Flow (PRD §1.9, WP-08).
// Renders all 17 node types with badges, preview snippets, and special handles:
// - condition: source handles 'true' (Green) and 'false' (Red)
// - interactive_message: source handles matching button.id
import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  NODE_CATALOGUE,
  type FlowNodeType,
  type ConditionNodeData,
  type InteractiveMessageNodeData,
} from '../../lib/flows';
import { TrashIcon } from '../icons';

export interface CustomFlowNodeData {
  nodeType: FlowNodeType;
  data: any;
  onDelete?: (id: string) => void;
}

function CustomFlowNodeComponent({ id, data, selected }: NodeProps) {
  const { t } = useI18n();
  const nodeType = (data?.nodeType || 'text') as FlowNodeType;
  const nodeData = (data?.data || {}) as Record<string, any>;
  const onDelete = data?.onDelete as ((id: string) => void) | undefined;

  const meta = NODE_CATALOGUE.find((item) => item.type === nodeType) || NODE_CATALOGUE[0];
  const title = t(meta.labelKey) || meta.defaultLabel;

  // Render snippet based on node type
  const renderContentPreview = () => {
    switch (nodeType) {
      case 'text':
        return (
          <p className="text-xs text-ink-2 dark:text-slate-300 line-clamp-3 whitespace-pre-wrap">
            {nodeData.text || '(Empty message)'}
          </p>
        );
      case 'image':
      case 'video':
      case 'audio':
      case 'file':
        return (
          <div className="space-y-1 text-xs text-ink-2 dark:text-slate-300">
            {nodeData.fileName && <p className="font-mono text-[11px] font-semibold">{nodeData.fileName}</p>}
            {nodeData.mediaUrl && <p className="truncate font-mono text-[10px] text-brand">{nodeData.mediaUrl}</p>}
            {nodeData.caption && <p className="italic text-[11px] text-ink-3 dark:text-slate-400">"{nodeData.caption}"</p>}
            {!nodeData.mediaUrl && !nodeData.fileName && !nodeData.caption && (
              <span className="text-ink-4 dark:text-slate-500">(Media attachment not set)</span>
            )}
          </div>
        );
      case 'location':
        return (
          <div className="text-xs text-ink-2 dark:text-slate-300 space-y-0.5">
            <p className="font-semibold">{nodeData.name || 'Location'}</p>
            <p className="text-[11px] text-ink-3 dark:text-slate-400">{nodeData.address || `${nodeData.latitude}, ${nodeData.longitude}`}</p>
          </div>
        );
      case 'ai_reply':
        return (
          <div className="text-xs text-ink-2 dark:text-slate-300">
            <p className="line-clamp-2 italic text-ink-3 dark:text-slate-400">
              {nodeData.systemPrompt ? `Prompt: ${nodeData.systemPrompt}` : 'Default AI Agent Response'}
            </p>
          </div>
        );
      case 'user_input_flow':
        return (
          <div className="space-y-1.5 text-xs text-ink-2 dark:text-slate-300">
            <p className="font-medium line-clamp-2">{nodeData.prompt || '(No question prompt)'}</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                → {`{{${nodeData.variableName || 'var'}}}`}
              </span>
              {nodeData.validationType && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  {nodeData.validationType}
                </span>
              )}
            </div>
          </div>
        );
      case 'interactive_message': {
        const inter = nodeData as InteractiveMessageNodeData;
        return (
          <div className="space-y-2 text-xs text-ink-2 dark:text-slate-300">
            <p className="line-clamp-2">{inter.body || '(Interactive message text)'}</p>
            {/* Render buttons with handle attachment indicators */}
            <div className="space-y-1 pt-1">
              {(inter.buttons || []).map((btn) => (
                <div
                  key={btn.id}
                  className="relative flex items-center justify-between rounded-lg border border-purple-200 bg-purple-50/70 px-2.5 py-1.5 text-[11px] font-semibold text-purple-800 dark:border-purple-900/60 dark:bg-purple-950/40 dark:text-purple-300"
                >
                  <span className="truncate pr-3">{btn.title}</span>
                  <span className="h-2 w-2 rounded-full bg-purple-500 flex-shrink-0" />
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={btn.id}
                    className="!h-3 !w-3 !bg-purple-600 !border-2 !border-white dark:!border-slate-900 !-end-1.5"
                  />
                </div>
              ))}
            </div>
          </div>
        );
      }
      case 'template_message':
        return (
          <div className="space-y-1 text-xs text-ink-2 dark:text-slate-300">
            <p className="font-semibold text-brand truncate">{nodeData.templateName || '(Template not selected)'}</p>
            <p className="text-[11px] text-ink-3 dark:text-slate-400">Lang: {nodeData.templateLanguage || 'en'}</p>
          </div>
        );
      case 'cta_url_button':
        return (
          <div className="space-y-1.5 text-xs text-ink-2 dark:text-slate-300">
            <p className="line-clamp-2">{nodeData.body || 'Click link below'}</p>
            <div className="rounded-lg border border-line bg-surface p-1.5 text-center text-[11px] font-semibold text-brand dark:border-slate-700 dark:bg-slate-800">
              🔗 {nodeData.buttonText || 'Open Link'}
            </div>
          </div>
        );
      case 'condition': {
        const cond = nodeData as ConditionNodeData;
        const ruleCount = cond.rules?.length || 0;
        return (
          <div className="space-y-1.5 text-xs text-ink-2 dark:text-slate-300">
            <p className="font-medium">
              {ruleCount} rule(s) matching <span className="font-bold uppercase text-brand">{cond.logicalOperator || 'and'}</span>
            </p>
            {cond.rules?.[0] && (
              <p className="font-mono text-[10px] text-ink-3 dark:text-slate-400 truncate">
                {cond.rules[0].field} {cond.rules[0].operator} {String(cond.rules[0].value ?? '')}
              </p>
            )}
          </div>
        );
      }
      case 'subscribe_sequence':
        return (
          <p className="text-xs text-ink-2 dark:text-slate-300">
            Sequence ID: <span className="font-mono font-semibold text-brand">{nodeData.sequenceId || '(Not set)'}</span>
          </p>
        );
      case 'label_assign':
        return (
          <div className="text-xs text-ink-2 dark:text-slate-300 space-y-0.5">
            <p>+ Add: {nodeData.addLabelIds?.length || 0} labels</p>
            <p>- Remove: {nodeData.removeLabelIds?.length || 0} labels</p>
          </div>
        );
      case 'random_number':
        return (
          <div className="text-xs text-ink-2 dark:text-slate-300">
            Range: [{nodeData.min ?? 1} .. {nodeData.max ?? 100}] → <code className="font-mono text-brand font-bold">{nodeData.variableName || 'rand'}</code>
          </div>
        );
      case 'http_api':
        return (
          <div className="text-xs text-ink-2 dark:text-slate-300">
            API Action: <span className="font-semibold text-brand">{nodeData.apiId || '(Not configured)'}</span>
          </div>
        );
      case 'google_sheet_fetch':
        return (
          <div className="text-xs text-ink-2 dark:text-slate-300">
            Sheet Fetch: <span className="font-semibold text-brand">{nodeData.fetchId || '(Not configured)'}</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        'relative min-w-[240px] max-w-[280px] rounded-2xl border bg-white shadow-md dark:bg-slate-900 transition-all',
        selected
          ? 'ring-2 ring-brand ring-offset-2 border-brand dark:ring-offset-slate-900'
          : 'border-line hover:border-slate-300 dark:border-slate-800 dark:hover:border-slate-700'
      )}
    >
      {/* Target Handle at Top */}
      <Handle
        type="target"
        position={Position.Top}
        className="!h-3.5 !w-3.5 !bg-brand !border-2 !border-white dark:!border-slate-900 !-top-2 transition-transform hover:scale-125"
      />

      {/* Header */}
      <div className="flex items-center justify-between border-b border-line px-3.5 py-2.5 dark:border-slate-800">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base" aria-hidden>{meta.icon}</span>
          <div className="min-w-0">
            <h4 className="truncate text-xs font-bold text-ink dark:text-white leading-tight">
              {title}
            </h4>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0 ms-2">
          {meta.badge && (
            <span className="rounded bg-brand/10 px-1.5 py-0.5 text-[9px] font-bold text-brand dark:bg-brand/20">
              {meta.badge}
            </span>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(id);
              }}
              title="Delete node"
              className="rounded p-1 text-ink-4 hover:bg-danger/10 hover:text-danger dark:text-slate-500"
            >
              <TrashIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Body Content */}
      <div className="p-3.5">
        {renderContentPreview()}
      </div>

      {/* Source Handles at Bottom */}
      {nodeType === 'condition' ? (
        /* CONDITION NODE: Two Source Handles ('true' and 'false') */
        <div className="flex items-center justify-between border-t border-line/60 bg-surface/40 px-3 py-2 text-[11px] font-bold dark:border-slate-800/60 dark:bg-slate-800/40 rounded-b-2xl">
          <div className="relative flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
            <span>True</span>
            <Handle
              type="source"
              position={Position.Bottom}
              id="true"
              className="!h-3.5 !w-3.5 !bg-emerald-600 !border-2 !border-white dark:!border-slate-900 !start-4 !-bottom-2 transition-transform hover:scale-125"
            />
          </div>

          <div className="relative flex items-center gap-1 text-rose-600 dark:text-rose-400">
            <span>False</span>
            <Handle
              type="source"
              position={Position.Bottom}
              id="false"
              className="!h-3.5 !w-3.5 !bg-rose-600 !border-2 !border-white dark:!border-slate-900 !end-4 !-bottom-2 transition-transform hover:scale-125"
            />
          </div>
        </div>
      ) : nodeType === 'interactive_message' ? (
        /* Interactive message buttons have handles rendered inside the body */
        null
      ) : (
        /* Standard Single Source Handle at Bottom */
        <Handle
          type="source"
          position={Position.Bottom}
          className="!h-3.5 !w-3.5 !bg-brand !border-2 !border-white dark:!border-slate-900 !-bottom-2 transition-transform hover:scale-125"
        />
      )}
    </div>
  );
}

export const CustomFlowNode = memo(CustomFlowNodeComponent);
export default CustomFlowNode;
